<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRegisterSW } from 'virtual:pwa-register/vue'
import { useAuthStore, useLiquidGlass, GLASS_TUNING } from '@claimshield/shared'

/**
 * The component stays mounted unconditionally (App.vue) so `useRegisterSW`
 * always registers the service worker — `injectRegister: false` in
 * vite.config.ts means this call IS the registration, and gating the mount
 * would stop update detection for anyone sitting on /login.
 *
 * What IS gated is the visible bar. "A new version of Ops is available" is
 * internal product voice, and /login is an unauthenticated surface — the same
 * leak LoginView.vue's own comments describe fixing for the "Operations"
 * overline. Staff see the prompt; a signed-out visitor sees nothing.
 */
const authStore = useAuthStore()

const { offlineReady, needRefresh, updateServiceWorker } = useRegisterSW({
  onRegisteredSW(swUrl) {
    // eslint-disable-next-line no-console
    console.info('[pwa] service worker registered:', swUrl)
  },
  onRegisterError(err) {
    // eslint-disable-next-line no-console
    console.error('[pwa] service worker registration error:', err)
  },
})

function reload() {
  updateServiceWorker(true) // posts SKIP_WAITING + reloads
}
function dismiss() {
  offlineReady.value = false
  needRefresh.value = false
}

// Only a waiting new version shows the bar. `offlineReady` fires on the
// worker's first install, but sw.ts precaches nothing (push only), so "ready
// to work offline" was a promise ops cannot keep (ops audit 2026-10-02, #8).
const showToast = computed(() => authStore.isAuthenticated && needRefresh.value)

/**
 * Liquid Glass — the brand's sixth signature element (knowledge/brand/tokens.md).
 * SMOKED, not clear: this bar floats over the ops app's dark chrome, and the
 * canon's rule is "never clear on dark" — the clear recipe washes out there.
 *
 * Tuned as a nav bar (-64/4), not a card (-112/6). The refraction band is a fixed
 * share of the *smaller* side, so on a ~48px-tall bar the card tuning smears the
 * message text. Chromium refracts; Safari and Firefox get a frosted blur.
 */
const toastEl = ref<HTMLElement | null>(null)
useLiquidGlass(toastEl, GLASS_TUNING.navBar)
</script>

<template>
  <div
    v-if="showToast"
    ref="toastEl"
    class="cs-pwa-toast"
    role="status"
    aria-live="polite"
  >
    <span class="cs-pwa-msg">
      A new version of Ops is available.
    </span>
    <button type="button" class="cs-pwa-btn cs-pwa-btn--primary" @click="reload">
      Reload
    </button>
    <button type="button" class="cs-pwa-btn" @click="dismiss">Dismiss</button>
  </div>
</template>

<style scoped>
/* Bottom-left is deliberate, not an oversight: this prompt is PERSISTENT, and
   the top-right lane belongs to PrimeVue's transient PToast stack (App.vue,
   baseZIndex 9999). Parking a bar that never auto-dismisses in that lane means
   every real toast stacks over or under it. */
.cs-pwa-toast {
  position: fixed;
  bottom: 1rem;
  left: 1rem;
  right: auto;
  z-index: 9999;
  display: flex;
  align-items: center;
  /* The message used to be `white-space: nowrap` inside a viewport-capped box,
     so a narrow window clipped it instead of reflowing. Wrapping the flex row
     drops the buttons onto their own line. */
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  /* The Curve: a floating surface is card-class (12px), not control-class (8px).
     The glass module reads this value for its map's corner radius, so the radius
     and the refraction stay in agreement automatically. */
  border-radius: var(--cs-radius-xl);
  /* Liquid Glass, SMOKED variant — the canon recipe verbatim (tokens.md).
     `theme('colors.secondary.800')` used to sit here as a flat fill; it is now the
     no-backdrop-support floor, so a browser without backdrop-filter still gets an
     opaque legible bar rather than transparent text over the app. */
  background: theme('colors.secondary.800');
  /* Canon smoked is .78 -> .64. This bar runs deeper (.90 -> .82) for a reason
     the canon's numbers do not cover: they assume glass over a UNIFORM backdrop.
     This one is ~600px wide, the ops sidebar is 232px, so the bar ALWAYS straddles
     the dark-chrome / light-content edge — and at canon alpha it reads as two
     different colours joined in the middle rather than as one object. Rendered
     comparison of .78/.64 vs .90/.82 vs near-opaque:
     lab/sketches/090-pwa-toast-glass/. */
  background: linear-gradient(135deg, rgba(26, 16, 53, 0.9), rgba(42, 26, 94, 0.82));
  border: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow:
    0 8px 28px rgba(26, 16, 53, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.14);
  /* The module overwrites backdrop-filter on mount (refraction or frosted
     fallback). This declaration is what renders in the window before that chunk
     lands, so the bar is never briefly a flat slab. */
  -webkit-backdrop-filter: blur(8px) saturate(1.4);
  backdrop-filter: blur(8px) saturate(1.4);
  /* Canon: white at 0.75+ on smoked — muted greys wash out when the surface
     floats over a light region. The message itself stays full white. */
  color: #fff;
  font-size: 0.875rem;
  max-width: calc(100vw - 2rem);
  overflow: hidden;
}
.cs-pwa-msg {
  flex: 1 1 12rem;
  min-width: 0;
}
.cs-pwa-btn {
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: transparent;
  color: #fff;
  padding: 0.35rem 0.75rem;
  /* The Curve, control tier = 8px. This was `--cs-radius-md` (6px), which is the
     MICRO tier — the one the canon reserves for checkboxes, chips and tags. A
     button is a control. */
  border-radius: var(--cs-radius-lg);
  cursor: pointer;
  font-size: 0.8125rem;
  /* Buttons sit ON the glass, so they must not read as a second sheet of it
     (canon: never glass on glass). Flat surfaces, tinted by the rim only. */
  transition: background-color 120ms ease, border-color 120ms ease, opacity 120ms ease;
}
/* Canon: on a dark surface gold leads the CTA. The previous #1d4ed8 was
   Tailwind blue-700 — no relation to any ClaimShield token, which is why the
   exact-match hex sweep (102cb1a8a) walked straight past it. Ink is
   purple-dark for a ~6:1 read on gold. */
.cs-pwa-btn--primary {
  background: var(--cs-gold);
  border-color: var(--cs-gold);
  color: var(--cs-purple-dark);
  font-weight: 600;
}
.cs-pwa-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.5);
}
.cs-pwa-btn--primary:hover {
  background: var(--cs-gold);
  border-color: var(--cs-gold);
  opacity: 0.9;
}
.cs-pwa-btn:focus-visible {
  outline: 2px solid var(--cs-gold);
  outline-offset: 2px;
}

/* The refraction is decoration, never meaning — and a moving backdrop is what
   reduced-motion users are asking us to stop. The frosted floor above still
   reads as glass without the module's live re-mapping. */
@media (prefers-reduced-motion: reduce) {
  .cs-pwa-toast { transition: none; }
  .cs-pwa-btn { transition: none; }
}
</style>
